/**
 * 推荐抽样工具 —— 后端返回稳定排序的大候选池，前端按 session seed 抽样打散。
 *
 * 同一 seed → 同一顺序（同次浏览稳定）；seed 变 → 顺序/集合变（重新进入变化）。
 * 用 mulberry32（确定性 PRNG）驱动 Fisher-Yates：可重现、零依赖，比 Math.random 更可控。
 */

// mulberry32：给定种子返回 [0,1) 伪随机函数；同一 seed 序列确定
export function mulberry32 (seed) {
  let a = seed >>> 0
  return function () {
    a = (a + 0x6D2B79F5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

// Fisher-Yates 带 seed 打散：不改原数组，返回新数组；同一 seed 同一结果
export function seededShuffle (arr, seed) {
  const rnd = mulberry32(seed)
  const a = (arr || []).slice()
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1))
    const tmp = a[i]; a[i] = a[j]; a[j] = tmp
  }
  return a
}

// 商品排序权重归一化（products.weight，语义冻结见 docs/DATABASE_FIELD_SEMANTICS.md）：
//   100（默认）=1.0 基准；0=0.5 下限；500=2.0 上限；超出夹紧。
// 分段线性防极端值——运营权重最多 4 倍采样概率差，不可能垄断曝光。
// NULL/非法值按默认 100 处理（=1.0）。
export function normalizeProductWeight (w) {
  if (w === null || w === undefined) return 1
  const n = Number(w)
  if (!isFinite(n)) return 1
  if (n <= 100) return 0.5 + 0.5 * Math.max(0, n) / 100
  return Math.min(2, 1 + (n - 100) / 400)
}

// 加权洗牌（Efraimidis-Spirakis A-Res）：每项 key = rnd()^(1/factor)，按 key 降序。
// 因子高 → 大概率靠前/被抽中；因子低 → 概率变小但恒 >0（随机探索保留）。
// 同 seed 同结果（mulberry32 驱动）。factor 缺省 1 = 退化为均匀随机洗牌。
function weightedShuffle (arr, seed, getFactor) {
  const rnd = mulberry32(seed)
  return (arr || [])
    .map(x => ({ x, k: Math.pow(rnd(), 1 / (getFactor ? getFactor(x) : 1)) }))
    .sort((a, b) => b.k - a.k)
    .map(e => e.x)
}

// 从 pool 按 seed 随机抽取 displayCount 个（集合+顺序都随 seed 变；池不足时返回全部）
export function sampleBySeed (pool, seed, displayCount) {
  const shuffled = seededShuffle(pool, seed)
  const n = Math.min(displayCount, shuffled.length)
  return shuffled.slice(0, Math.max(0, n))
}

// 加权抽样：pool 须已按权重降序（后端排序即权重信号：推荐 score / 热销序）。
// 把 top 区（前 topRatio）铺成「高曝光」、探索区穿插进全流——用 Bresenham 整型比例合并
// 让 top 均匀前置（首项恒为 top），保留权重趋势；同 seed 同序，seed 变则两区各自重洗 +
// 合并顺序变（非固定重复）。displayCount 默认返回全池重排；池 ≤1 原样返回。
//
// 区内洗牌为商品权重加权（normalizeProductWeight，0.5~2.0）：运营排序权重轻推高 weight
// 商品的采样概率与位次（池位置=行为信号仍主导 top/explore 分区，weight 只影响区内次序，
// 实际曝光影响 ~10-20%）；低 weight 恒有探索位，factor 上限 2.0 防垄断。
//
// 与 sampleBySeed 的区别：sampleBySeed 是均匀随机（不保留权重趋势，长尾与热门等概率）；
// weightedSample 让高权重商品稳定高曝光、低权重商品也有探索位——首页瀑布流专用。
export function weightedSample (pool, seed, displayCount, topRatio = 0.3) {
  const arr = (pool || []).slice()
  const want = displayCount != null ? Math.max(0, displayCount) : arr.length
  if (arr.length <= 1) return arr.slice(0, want)

  const wf = (p) => normalizeProductWeight(p && p.weight)
  const topCount = Math.max(1, Math.min(arr.length - 1, Math.round(arr.length * topRatio)))
  const top = weightedShuffle(arr.slice(0, topCount), seed, wf)
  const explore = weightedShuffle(arr.slice(topCount), seed + 1, wf) // +1 解耦两区随机序列

  const topW = top.length
  const exploreW = explore.length
  const out = []
  let ti = 0
  let ei = 0
  let topErr = 0
  let exploreErr = 0
  // Bresenham 比例合并：哪个区落后于其目标比例就补哪个；<= 让 top 先走 → 首项恒为 top
  while ((ti < topW || ei < exploreW) && out.length < want) {
    if (ti < topW && (ei >= exploreW || topErr <= exploreErr)) {
      out.push(top[ti++]); topErr += exploreW
    } else {
      out.push(explore[ei++]); exploreErr += topW
    }
  }
  return out
}

// 统一库存判定（stock 语义：INTEGER，>0=有货；扣减走 inventoryLedger 条件更新不会为负）。
// 纯函数，全推荐链路（详情/瀑布流/精选）唯一判定口——各页面禁止散落 p.stock > 0。
export function isProductInStock (p) {
  return !!(p && Number(p.stock) > 0)
}

// 库存分层抽样：有货组、无货组各自走同一 sampler，输出拼接（无货整体沉底，组内原随机/权重逻辑不变）。
// 不做无货过滤——两组都进序列，只是先后。空组 sampler 自然返回 []；全有货 = 退化为原 sampler 行为。
// sampler 收全长洗牌/抽样器（displayCount 缺省或=池长）；不做跨组配额分配（现有调用点全是全长场景）。
export function stockFirst (pool, sampler) {
  const arr = (pool || []).filter(p => p && p.id)
  const inStock = arr.filter(isProductInStock)
  const outStock = arr.filter(p => !isProductInStock(p))
  return sampler(inStock).concat(sampler(outStock))
}

// 详情页「更多推荐」强相关头部：pool 须按 _score 降序（后端 /related 返回序）。
// 结构：strongPool = Top strongSize（默认 8，池不足取全部）→ 第 1 位分差保护 →
//   其余按 score 加权抽 headSize-1（默认共前 4 = 两列瀑布流前两排）。
//   第 1 位规则：Top1 对 Top2 领先幅度 ≥ closeGap（默认 15%）锁定 Top1；
//   接近则两者按 score 加权二选一——避免机械固定，也不让弱者经常抢首位。
//   2~4 位 Efraimidis-Spirakis 加权：高分大概率入选，低分偶尔出现（探索性）。
// 返回 { head, rest }：rest = 全池移除 head（strongPool 落选者回落随机区，仍可后续出现）。
// 库存分层：strong 候选先按有货稳定分区（层内保持 _score 降序）——strong 池有货优先入选，
// 有货强相关不足 headSize 时无货自然补位（分区+slice，无补位特判分支）；
// Top1 保护/加权随机在分区后的序列上照常运行（层内相对分差不受影响）。rest 的沉底由调用方
// stockFirst 分层洗牌完成（本函数只管头部候选序，不重复分层）。
// 无 _score 字段（非 /related 数据）→ head=[], rest=pool 拷贝，退化纯随机（自然降级）。
// 同 seed 同结果：页面生命周期内稳定；seed 变（重进详情）→ 组合变。
export function buildStrongHead (pool, seed, { headSize = 4, strongSize = 8, closeGap = 0.15 } = {}) {
  const arr = (pool || []).filter(p => p && p.id && typeof p._score === 'number')
  if (!arr.length) return { head: [], rest: (pool || []).slice() }
  const strong = [...arr.filter(isProductInStock), ...arr.filter(p => !isProductInStock(p))].slice(0, strongSize)
  const [a, b] = strong
  let first = a
  if (b && (a._score - b._score) / a._score < closeGap) {
    // 平方权重：28% 分差 → ~66/34（score 线性权重只给 58/42，区分太弱）
    const rnd = mulberry32(seed)
    const wa = a._score * a._score
    const wb = b._score * b._score
    first = rnd() < wa / (wa + wb) ? a : b
  }
  // 加权抽取同样分层：有货组先抽满 headSize-1，不足再无货组补位（配额式，同 buildFeatured 的 fill）。
  // factor 各组内线性归一到 [0.5, 1.0]（与 normalizeProductWeight 同哲学；组内归一防跨组分数差稀释区分度）。
  // rnd2 同一流顺序消耗：先有货组后无货组，同 seed 同结果。
  const pick = strong.filter(p => p !== first)
  const rnd2 = mulberry32(seed + 1)
  const draw = (group) => {
    if (!group.length) return []
    const sMin = Math.min(...group.map(p => p._score))
    const sMax = Math.max(...group.map(p => p._score))
    const factor = p => (sMax === sMin ? 1 : 0.5 + 0.5 * (p._score - sMin) / (sMax - sMin))
    return group
      .map(p => ({ p, k: Math.pow(rnd2(), 1 / factor(p)) }))
      .sort((x, y) => y.k - x.k)
      .map(e => e.p)
  }
  const want = Math.max(0, headSize - 1)
  const inDrawn = draw(pick.filter(isProductInStock)).slice(0, want)
  const outDrawn = draw(pick.filter(p => !isProductInStock(p))).slice(0, want - inDrawn.length)
  const head = [first, ...inDrawn, ...outDrawn]
  const headIds = new Set(head.map(p => p.id))
  const rest = (pool || []).filter(p => !(p && headIds.has(p.id)))
  return { head, rest }
}

// 首页精选推荐组装（candidate → final display 层，不含数据拉取）：
//   管理员精选池非空时 Home.vue 走 sampleBySeed 纯池轮换（ Featured Rotation，均匀无权重），
//   本函数只服务 N=0 回退链：
//   1) 从个性化候选池 weightedSample 加权抽（score 高=高曝光，非稳定 topN）
//   2) 再不满从兜底池（冷启动混合池/热销）加权抽
// 两源之间按 product.id 去重；seed 由调用方每次首页进入重新生成（重进变、同页稳定）。
// exclude = 上一批展示的 product_id（sessionStorage 级短期防重复：优先避开，候选不足才回补）。
export function buildFeatured ({ rec = [], hot = [], seed, target = 4, exclude = [] }) {
  const used = new Set()
  const out = []
  const push = (p) => {
    if (p && p.id && !used.has(p.id) && out.length < target) { used.add(p.id); out.push(p) }
  }
  // 候选池先滤掉已占位商品再抽——否则抽到重复会被去重静默丢弃导致缺位
  const fill = (pool, banned) => {
    if (out.length >= target) return
    const fresh = (pool || []).filter(p => p && p.id && !used.has(p.id) && !banned.has(p.id))
    if (!fresh.length) return
    // 库存分层：有货组先抽满配额，不足再无货组补位（两组集合不相交，同 seed 无重复风险）
    const inS = fresh.filter(isProductInStock)
    const outS = fresh.filter(p => !isProductInStock(p))
    const want = target - out.length
    const picked = weightedSample(inS, seed, want)
    if (picked.length < want && outS.length) picked.push(...weightedSample(outS, seed, want - picked.length))
    picked.forEach(push)
  }
  const banned = new Set(exclude)
  fill(rec, banned)
  fill(hot, banned)
  // 候选不足（排除后池子太小）→ 放开排除回补，宁可用旧商品也不缺位
  if (out.length < target && banned.size) {
    fill(rec, new Set())
    fill(hot, new Set())
  }
  return out
}
